/* eslint-disable array-bracket-newline */
import { useEffect, useMemo, useState } from 'react';
import {
  ICoinNewsAI,
  ICoinWithMarket,
  IOrganicTradesInfo,
  ITicker,
  ITokenInfo,
  ITokenNewsItem,
  Network,
  enums,
} from '@kattana-io/common';
import useTickerLivePrice from '../Watchlist/hooks/useTickerLivePrice';
import { ITickerDisplayData } from '../../hooks/useTicker';
import { IGoPlusTokenData, getGoPlusTokenInfo } from '../../helpers/goPlusApi';
import { KattanaAPI } from '../../store/createStore';
import {
  PoolWidgetRow,
  getCexPoolRows,
  getDexPoolRows,
  getGoPlusSecurityRows,
} from './helpers/rows';
import { getCexPoolInfo, getDexPoolInfo, getScoreInfo } from './helpers/info';

export interface IPoolWidgetLinks {
  website?: string;
  coingecko_id?: string;
  coinmarketcap_id?: string;
  telegram?: string;
  twitter_id?: string;
  logo?: string;
}

export interface IPoolWidgetNews {
  ai: ICoinNewsAI;
  items: ITokenNewsItem[];
}

export interface IPoolWidgetScore {
  points: number;
  rows: PoolWidgetRow[];
}

export interface IPoolCoinInfo {
  links: IPoolWidgetLinks | null;
  news: IPoolWidgetNews | null;
}

const useTickerPool = (item: ITicker, ticker: ITickerDisplayData) => {
  const [cexCoin, setCexCoin] = useState<ICoinWithMarket | null>(null);
  const [dexCoin, setDexCoin] = useState<ITokenInfo | null>(null);
  const [goPlusData, setGoPlusData] = useState<IGoPlusTokenData | null>(null);
  const [organicTradesData, setOrganicTradesData] =
    useState<IOrganicTradesInfo | null>(null);
  const [poolInfo, setPoolInfo] = useState<IPoolCoinInfo>({
    links: null,
    news: null,
  });
  const [scoreInfo, setScoreInfo] = useState<IPoolWidgetScore | null>(null);
  const [rows, setRows] = useState<PoolWidgetRow[]>([]);
  const [extraRows, setExtraRows] = useState<PoolWidgetRow[]>([]);
  const [sameBaseTickers, setSameBaseTickers] = useState<ITicker[]>([]);
  const [loading, setLoading] = useState(true);

  const priceInfo = useTickerLivePrice(item);

  // 1 step: get all needed data for pool widget tabs and rows
  useEffect(() => {
    const controller = new AbortController();
    // reset all previous data
    setLoading(true);
    setPoolInfo({ links: null, news: null });
    setScoreInfo(null);
    setGoPlusData(null);
    setOrganicTradesData(null);
    setDexCoin(null);
    setCexCoin(null);

    if (item.kind === enums.ETickerKind.CEX) {
      // fetch coin info
      KattanaAPI.coin(ticker.base, true, true, controller).then((data) => {
        if (!controller.signal.aborted) {
          setCexCoin(data);
          setPoolInfo(getCexPoolInfo(data?.coin));
          setLoading(false);
        }
      });
      // fetch same base tickers
      KattanaAPI.tickers({
        searchRaw: `${ticker.base}/`,
        sort: { name: 'volume', order: 'DESC' },
        mobile: true,
      }).then((data) => {
        const parsed = data
          ?.filter((item) => item.id !== ticker.id)
          .slice(0, 3);
        setSameBaseTickers(parsed || []);
      });
    } else {
      // fetch coin info
      KattanaAPI.tokenInfo(
        ticker.chain,
        ticker.base_address,
        {
          news: true,
          holders_count: true,
        } as any,
        {
          signal: controller.signal,
        }
      ).then((data) => {
        setDexCoin(data);
        setPoolInfo(getDexPoolInfo(data, ticker.chain, ticker.base_address));
      });
      // fetch organic trades info
      KattanaAPI.organicTradesInfo(ticker.chain, item.address).then((data) => {
        setOrganicTradesData(data);
      });
      // fetch GoPlus Security data
      getGoPlusTokenInfo(
        Network.fromChain(ticker.chain).toDecimal(),
        ticker.base_address,
        controller
      ).then((data) => {
        setGoPlusData(data);
        setLoading(false);
      });
      // fetch and parse to rows and points coin score data for the corresponding tab
      KattanaAPI.tokenScore(ticker.chain, ticker.base_address, {
        signal: controller.signal,
      }).then((data) => {
        setScoreInfo(getScoreInfo(data));
      });
      // fetch same base tickers
      KattanaAPI.tickers({
        searchRaw: `${ticker.base}/`,
        sort: { name: 'liquidity', order: 'DESC' },
        mobile: true,
      }).then((data) => {
        const parsed = data
          ?.filter((item) => item.id !== ticker.id)
          .slice(0, 3);
        setSameBaseTickers(parsed || []);
      });
    }
    return () => controller.abort();
  }, [item.id]);

  // 2 step: parse and save all rows for main tab
  useEffect(() => {
    if (item.kind === enums.ETickerKind.CEX) {
      // parse cex rows
      const cexRows = getCexPoolRows(item, priceInfo, cexCoin);
      setRows(cexRows);
    } else {
      // parse main dex rows
      const generalRows = getDexPoolRows(
        item,
        ticker,
        priceInfo,
        dexCoin,
        goPlusData,
        organicTradesData
      );
      // parse extra dex rows (GoPlus Security ones)
      const goPlusSecurityRows = getGoPlusSecurityRows(goPlusData);
      setRows(generalRows);
      setExtraRows(goPlusSecurityRows);
    }
  }, [cexCoin, dexCoin, goPlusData, priceInfo, organicTradesData]);

  const data = useMemo(
    () => ({
      loading,
      rows,
      extraRows,
      scoreInfo,
      poolInfo,
      sameBaseTickers,
    }),
    [extraRows, loading, poolInfo, rows, sameBaseTickers, scoreInfo]
  );

  return data;
};

export default useTickerPool;
